import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Check, X } from "lucide-react";

/**
 * One-time, dismissible prompt for accounts created before usernames were
 * mandatory. Not a blocking gate — users can dismiss it and keep signing in
 * with email. Rendered from ClientDashboard/CounselorDashboard when
 * `user.username` is missing.
 */
export function UsernamePrompt({ onSet }) {
  const [open, setOpen] = useState(true);
  const [username, setUsername] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uCheck, setUCheck] = useState({ status: "idle", reason: "" });

  useEffect(() => {
    if (!username) { setUCheck({ status: "idle", reason: "" }); return; }
    setUCheck({ status: "checking", reason: "" });
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/auth/username-available", { username });
        setUCheck({
          status: data.available ? "ok" : "taken",
          reason: data.reason || (data.available ? "" : "That username is taken."),
        });
      } catch (e) {
        setUCheck({ status: "taken", reason: formatApiError(e) });
      }
    }, 350);
    return () => clearTimeout(t);
  }, [username]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    if (uCheck.status !== "ok") { setErr(uCheck.reason || "Please pick an available username."); return; }
    setBusy(true); setErr("");
    try {
      await api.post("/auth/me/username", { username });
      toast.success("Username set");
      setOpen(false);
      if (typeof onSet === "function") await onSet();
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="username-prompt" className="card-soft p-5 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <div className="text-sm font-medium">Pick a username</div>
          <div className="text-xs" style={{ color: "var(--text-secondary)" }}>
            Lets you sign in with a username instead of your email. Takes a few seconds.
          </div>
        </div>
        <form onSubmit={submit} className="flex items-center gap-2 flex-wrap">
          <input data-testid="username-prompt-input" value={username}
            onChange={e => setUsername(e.target.value)}
            maxLength={24} placeholder="e.g. writer_23"
            className="rounded-xl border border-[color:var(--border-soft)] px-3 py-2 text-sm focus:outline-none focus:border-[color:var(--brand)]" />
          {username && uCheck.status === "ok" && <Check data-testid="username-prompt-ok" className="w-4 h-4" style={{ color: "var(--brand)" }} />}
          {username && uCheck.status === "taken" && <X data-testid="username-prompt-taken" className="w-4 h-4" style={{ color: "var(--accent)" }} />}
          <button data-testid="username-prompt-submit" disabled={busy || uCheck.status !== "ok"}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed">
            {busy ? "Saving…" : "Save"}
          </button>
          <button type="button" data-testid="username-prompt-dismiss" onClick={() => setOpen(false)}
            className="text-xs underline" style={{ color: "var(--text-secondary)" }}>
            Not now
          </button>
        </form>
      </div>
      {err && <div data-testid="username-prompt-error" className="text-xs mt-2" style={{ color: "var(--accent)" }}>{err}</div>}
    </div>
  );
}
